import { useUser } from "@/contexts/user-provider";

import { PERMISSIONS, hasPermission } from "@/lib/permissions";

/**
 * Whether the current user may change tasks at all.
 *
 * Every task mutation route — create, update, complete, delete, comments,
 * subscribers — is behind `tasks:write`, while reading is behind `tasks:read`.
 * The two are separate permissions, so a role can grant one without the other
 * and land the user on a screen full of controls that only ever return 403.
 *
 * One hook rather than a check per screen: the tasks page, the panel, the
 * table, the kanban board and the detail modal all show the same task and have
 * to agree about whether it can be edited.
 */
export function useCanWriteTasks(): boolean {
  const { currentUser } = useUser();
  return hasPermission(currentUser, PERMISSIONS.TASKS_WRITE);
}

/**
 * Whether the current user may hand an existing task to someone else.
 *
 * Assigning is split out of `tasks:write` so a front desk lead can pass out the
 * tasks the alert rules generate without being able to create or edit any. The
 * backend expands `tasks:write` into this permission, so a user who can edit
 * tasks always passes this check too.
 */
export function useCanAssignTasks(): boolean {
  const { currentUser } = useUser();
  return hasPermission(currentUser, PERMISSIONS.TASKS_ASSIGN);
}
